import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, ReplaySubject } from "rxjs";
import { Coords, Mote, MoteEventType, PaddingOptions } from "../models";
import { MapService } from "../providers/map.service";
import { MoteEventService } from "./mote-event.service";

export enum MapMode {
    DEFAULT = "DEFAULT",
    TOUR_EXPLORER = "TOUR_EXPLORER",
    STORY_EXPLORER = "STORY_EXPLORER"
}

export type MapState = { mapMode: MapMode, tour: Mote, mapMotes: Mote[] };

@Injectable({ providedIn: 'root' })

export class MapOverlayService {

    public mapModeChange: BehaviorSubject<MapState> = new BehaviorSubject<MapState>({ mapMode: MapMode.DEFAULT, tour: null, mapMotes: [] });
    public mapMode: MapMode;
    // Store tour map position and tour selected
    private tourSelected: Mote;
    private mapPos: Coords;
    private mapZoom: number;

    constructor(private mapService: MapService, private moteEventService: MoteEventService) { }

    public watchMapMode(): Observable<MapState> {
        return this.mapModeChange.asObservable();
    }

    /**
     * Update map mode and set map motes
     */
    public updateMapMode(mapMode: MapMode, tour?: Mote, mapMotes?: Mote[], speed?: number, paddingOptions?: PaddingOptions): Promise<Coords> {
        this.mapMode = mapMode;
        this.mapModeChange.next({ mapMode: mapMode, tour: tour, mapMotes: mapMotes })
        switch(mapMode) {
            // Handle default user augmented mote map in map overlay via events (TODO: this is a mess - clean up this double system one day?)
            case MapMode.DEFAULT: return;
            case MapMode.STORY_EXPLORER: return this.updateStoryMap(tour, mapMotes, speed, paddingOptions);
            case MapMode.TOUR_EXPLORER: return this.updateTourMap(mapMotes, speed, paddingOptions);
            default: console.warn("No map updates are related to the requested map mode."); return;
        }
    }

    /**
     * Story map: One tour presented by route and motes
     */
    private async updateStoryMap(tour: Mote, mapMotes: Mote[], speed?: number, paddingOptions?: PaddingOptions): Promise<Coords> {
        await this.mapService.awaitInit();
        // Save tour map position and zoom
        if(this.mapMode == MapMode.TOUR_EXPLORER) {
            this.mapPos = this.mapService.getMapCenter();
            this.mapZoom = this.mapService.getMapZoom();
        }
  
        // Close any previously opened story modals on map
        this.moteEventService.emitMoteEvent({ type: MoteEventType.MAP_MOTE_UNSELECTED });

        this.mapService.hideLayer('tourClickLayer');
        // TODO Deprecate setMotes or setStories or both? We have many different variants that does the same
        await this.mapService.setMotes([tour, ...mapMotes]);
        this.mapService.showTour();
        if(mapMotes?.length <= 0) return;

        return this.mapService.fitMapBoundsToMotes(mapMotes, speed || 1.2, paddingOptions || { top: 130, bottom: 110, left: 100, right: 100 }, 16, 0, false);
    }

    /**
     * Tour map: All/filtered published tours
     */
    private async updateTourMap(mapMotes: Mote[], speed?: number, paddingOptions?: PaddingOptions): Promise<Coords> {
        await this.mapService.awaitInit();
        this.mapService.hideTour();
        this.mapService.setTours(mapMotes);
        this.mapService.showLayer('tourClickLayer');
        // Restore map position and zoom if this is a return to tour map
        if(this.mapPos && this.mapZoom) return this.mapService.flyTo(this.mapPos, this.mapZoom, 0);
        // Otherwise fit map to tour motes
        if(mapMotes?.length <= 0) return;
        return this.mapService.fitMapBoundsToMotes(mapMotes, speed || 1.2, paddingOptions || { top: 130, bottom: 110, left: 100, right: 100 }, 16, 10000, false);
    }

    /**
     * Fly to selected tour
     */
    public flyToTour(tour: Mote) {
        if(!tour) return;
        this.tourSelected = tour;
        this.mapService.flyTo(tour.position, null, 1, 5000);
    }

    public getSelectedTour(): Mote {
        return this.tourSelected;
    }
}
